import React from 'react';
import { FiFolder, FiArchive, FiTrendingUp, FiClock } from 'react-icons/fi';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Project {
  id: number;
  name: string;
  description: string;
  provider: string;
  architectureType?: string;
  lastModified: string;
  architectureCount: number;
  tags?: string[];
  archived?: boolean;
}

interface ProjectsDashboardProps {
  projects: Project[];
  onClose: () => void;
}

const ProjectsDashboard: React.FC<ProjectsDashboardProps> = ({ projects, onClose }) => {
  // Calculate statistics
  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => !p.archived).length;
  const archivedProjects = projects.filter(p => p.archived).length;
  const totalArchitectures = projects.reduce((sum, p) => sum + (p.architectureCount || 0), 0);

  // Group by provider
  const providerData = projects.reduce((acc: Record<string, number>, project) => {
    acc[project.provider] = (acc[project.provider] || 0) + 1;
    return acc;
  }, {});

  const providerChartData = Object.entries(providerData).map(([name, value]) => ({
    name,
    value,
  }));

  // Group by architecture type
  const archTypeData = projects.reduce((acc: Record<string, number>, project) => {
    const type = project.architectureType || 'Standard';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const archTypeChartData = Object.entries(archTypeData).map(([name, count]) => ({
    name,
    count,
  }));

  // Recent activity (last 7 days)
  const last7Days = new Date();
  last7Days.setDate(last7Days.getDate() - 7);
  const recentProjects = projects.filter(p => new Date(p.lastModified) > last7Days);

  // Colors for charts
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Fonction pour le label du PieChart
  const renderProviderLabel = (entry: any) => {
    const percent = (entry.percent * 100).toFixed(0);
    return `${entry.name}: ${percent}%`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          width: '100%',
          maxWidth: 1200,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 32px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
              Tableau de bord
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 14, opacity: 0.9 }}>
              Vue d'ensemble de vos projets GenIaC
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Fermer
          </button>
        </div>

        <div style={{ padding: 32 }}>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 32 }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: 24,
                borderRadius: 12,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <FiFolder size={24} />
                <span style={{ fontSize: 14, opacity: 0.9 }}>Total Projets</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{totalProjects}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                {activeProjects} actifs
              </div>
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                padding: 24,
                borderRadius: 12,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <FiTrendingUp size={24} />
                <span style={{ fontSize: 14, opacity: 0.9 }}>Architectures</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{totalArchitectures}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                {(totalArchitectures / totalProjects || 0).toFixed(1)} par projet
              </div>
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                padding: 24,
                borderRadius: 12,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <FiClock size={24} />
                <span style={{ fontSize: 14, opacity: 0.9 }}>Activité récente</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{recentProjects.length}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                7 derniers jours
              </div>
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                padding: 24,
                borderRadius: 12,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <FiArchive size={24} />
                <span style={{ fontSize: 14, opacity: 0.9 }}>Archivés</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{archivedProjects}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                {((archivedProjects / totalProjects) * 100 || 0).toFixed(0)}% du total
              </div>
            </div>
          </div>

          {/* Charts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 32 }}>
            {/* Provider Distribution */}
            <div
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: 12,
                padding: 24,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              }}
            >
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: '#111827' }}>
                Répartition par fournisseur
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={providerChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderProviderLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {providerChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Architecture Type Distribution */}
            <div
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: 12,
                padding: 24,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              }}
            >
              <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: '#111827' }}>
                Types d'architecture
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={archTypeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Projects */}
          <div
            style={{
              marginTop: 32,
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: 24,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600, color: '#111827' }}>
              Projets récents
            </h3>
            {recentProjects.length === 0 ? (
              <p style={{ color: '#9CA3AF', textAlign: 'center', padding: 20 }}>
                Aucune activité récente
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {recentProjects.slice(0, 5).map((project) => (
                  <div
                    key={project.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      background: '#F9FAFB',
                      borderRadius: 8,
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                        {project.name}
                      </div>
                      <div style={{ fontSize: 13, color: '#6B7280' }}>
                        {project.provider}{project.architectureType ? ` • ${project.architectureType}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'right' }}>
                      {new Date(project.lastModified).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsDashboard;
